import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLive } from "@/contexts/LiveContext";
import TheFlip from "@/components/TheFlip";
import ShareBar from "@/components/ShareBar";
import DebateRoom from "@/components/DebateRoom";
import DebateEssays from "@/components/DebateEssays";
import TrendingTicker from "@/components/TrendingTicker";
import { CommentItem, withMyReaction, netScore } from "@/components/CommentThread";
import { CategoryBadge } from "@/lib/categories";
import { getAnonId, getVote, setVote } from "@/lib/voteStore";
import { VoteBurst, FloatingPlusOne, haptic } from "@/components/VoteFX";
import { guestWallReached, recordGuestVote, guestNudge, openAuth, openAuthWall } from "@/lib/guestGate";
import { can } from "@/lib/roles";
import { UserAvatar } from "@/components/Navbar";
import { ArrowLeft, ArrowRight, MessageCircle, Send, Flame, Sparkles } from "lucide-react";
import { toast } from "sonner";

const COMMENTS_PER_VOTE = 5;
const REPLY_WEIGHT = 0.4; // a reply nudges the compass less than a top-level comment

export default function IssueDetail() {
  const { topicId } = useParams();
  const { user, api } = useAuth();
  const { subscribe } = useLive();
  const [topic, setTopic] = useState(null);
  const [trending, setTrending] = useState([]);
  const [userVote, setUserVote] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentSort, setCommentSort] = useState("top");
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [voteAnim, setVoteAnim] = useState(0);
  const [kickSide, setKickSide] = useState(null);
  const [sessionStreak, setSessionStreak] = useState(() => Number(sessionStorage.getItem("fv_session_streak") || 0));
  const [submittingComment, setSubmittingComment] = useState(false);
  const shareCardRef = useRef(null);

  const fetchTopic = useCallback(async () => {
    try {
      const { data } = await api.get(`/topics/${topicId}`);
      setTopic(data);
    } catch (err) {
      console.error("Failed to fetch topic:", err);
    } finally {
      setLoading(false);
    }
  }, [api, topicId]);

  const fetchUserVote = useCallback(async () => {
    const local = getVote(topicId);
    if (local) { setUserVote(local); return; }
    if (!user) return;
    try {
      const { data } = await api.get("/votes/user");
      const v = data.find((v) => v.topic_id === topicId);
      setUserVote(v?.side || null);
    } catch {}
  }, [api, user, topicId]);

  const fetchComments = useCallback(async () => {
    try {
      const { data } = await api.get(`/topics/${topicId}/comments`, { params: { sort: commentSort } });
      setComments(data.map((c) => withMyReaction(c, user?.user_id)));
    } catch {}
  }, [api, topicId, commentSort, user]);

  useEffect(() => { fetchTopic(); }, [fetchTopic]);
  useEffect(() => { fetchUserVote(); }, [fetchUserVote]);
  useEffect(() => { fetchComments(); }, [fetchComments]);

  useEffect(() => {
    api.get("/trending").then(({ data }) => setTrending(data.trending || [])).catch((err) => console.debug("trending fetch failed", err));
  }, [api]);

  // Live updates via shared socket
  useEffect(() => subscribe((msg) => {
    if (msg.type === "vote_update" && msg.topic_id === topicId) {
      setTopic((prev) => prev ? { ...prev, votes_a: msg.votes_a, votes_b: msg.votes_b, total_votes: msg.total_votes } : prev);
      setVoteAnim((v) => v + 1);
    }
  }), [subscribe, topicId]);

  const handleVote = async (side) => {
    if (!user && guestWallReached(topicId)) { openAuthWall(); return; }
    setKickSide(side);
    haptic(28);
    setVoting(true);
    try {
      const { data } = await api.post("/votes", { topic_id: topicId, side, anon_id: getAnonId() });
      setUserVote(side);
      setVote(topicId, side);
      try {
        const voted = JSON.parse(sessionStorage.getItem("fv_session_voted") || "[]");
        if (!voted.includes(topicId)) {
          voted.push(topicId);
          sessionStorage.setItem("fv_session_voted", JSON.stringify(voted));
          sessionStorage.setItem("fv_session_streak", String(voted.length));
          setSessionStreak(voted.length);
        }
      } catch {}
      setTopic((prev) => prev ? { ...prev, votes_a: data.votes_a, votes_b: data.votes_b } : prev);
      setVoteAnim((v) => v + 1);
      if (user) {
        toast.success(userVote ? "Vote changed!" : "Vote cast!");
        if (data.streak?.current_streak > 1) {
          toast.success(`${data.streak.current_streak}-day voting streak!`, { icon: <Flame className="w-4 h-4 text-orange-500" /> });
        }
      } else {
        const n = guestNudge(recordGuestVote(topicId));
        toast(n.msg, n.action ? { action: { label: "Sign up", onClick: openAuth } } : undefined);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to vote");
    } finally {
      setVoting(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !user) return;
    setSubmittingComment(true);
    try {
      const { data } = await api.post(`/topics/${topicId}/comments`, { text: commentText });
      setComments((prev) => [withMyReaction(data, user.user_id), ...prev]);
      setCommentText("");
    } catch (err) {
      toast.error("Failed to post comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleReply = async (target, text) => {
    if (!user) { toast.error("Sign in to reply"); window.dispatchEvent(new CustomEvent("fv-open-auth")); return; }
    const parentId = target.parent_id || target.comment_id; // flatten replies-to-replies under the root (Facebook style)
    try {
      const { data } = await api.post(`/topics/${topicId}/comments`, { text, parent_id: parentId });
      setComments((prev) => [...prev, withMyReaction(data, user.user_id)]);
      toast.success("Reply posted!");
    } catch {
      toast.error("Failed to post reply");
    }
  };

  const handleReact = async (commentId, type) => {
    if (!user) { toast.error("Sign in to react"); window.dispatchEvent(new CustomEvent("fv-open-auth")); return; }
    try {
      const { data } = await api.post(`/comments/${commentId}/react`, { type });
      setComments((prev) =>
        prev.map((c) => (c.comment_id === commentId ? { ...c, my_reaction: data.my_reaction, reaction_counts: data.reaction_counts } : c))
      );
    } catch {
      toast.error("Failed to react");
    }
  };

  const handleReport = async (commentId, reason) => {
    if (!user) { toast.error("Sign in to report"); window.dispatchEvent(new CustomEvent("fv-open-auth")); return; }
    try {
      const { data } = await api.post(`/comments/${commentId}/report`, { reason });
      toast.success(data.message || "Reported for review");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to report");
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/comments/${commentId}`);
      setComments((prev) => prev.filter((c) => c.comment_id !== commentId && c.parent_id !== commentId));
      toast.success("Comment removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-4 bg-gray-100 rounded w-20" />
          <div className="h-8 bg-gray-100 rounded w-3/4" />
          <div className="h-48 bg-gray-100 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-center">
        <p className="text-neutral">Debate not found.</p>
        <Link to="/" className="text-view-a text-sm font-semibold mt-2 inline-block">Go home</Link>
      </div>
    );
  }

  const total = (topic.votes_a || 0) + (topic.votes_b || 0);
  const pctA = total > 0 ? Math.round((topic.votes_a / total) * 100) : 50;
  const pctB = 100 - pctA;

  // Comment influence on the compass: 5 weighted comment-units on a side = 1 vote-equivalent.
  // Top-level comments count as 1 unit; replies count less (REPLY_WEIGHT).
  const weightOf = (c) => (c.parent_id ? REPLY_WEIGHT : 1);
  const canModerate = can(user, "moderate");
  const commentsA = comments.filter((c) => c.voted_side === "a").reduce((s, c) => s + weightOf(c), 0);
  const commentsB = comments.filter((c) => c.voted_side === "b").reduce((s, c) => s + weightOf(c), 0);
  const mySideComments = userVote === "a" ? commentsA : userVote === "b" ? commentsB : 0;
  const rem = mySideComments % COMMENTS_PER_VOTE;
  const toNext = Math.round((COMMENTS_PER_VOTE - rem) * 10) / 10;
  const progressPct = (rem / COMMENTS_PER_VOTE) * 100;

  // Build threaded comments
  const topLevel = comments.filter((c) => !c.parent_id);
  const repliesByParent = {};
  comments.filter((c) => c.parent_id).forEach((c) => {
    (repliesByParent[c.parent_id] = repliesByParent[c.parent_id] || []).push(c);
  });
  Object.values(repliesByParent).forEach((arr) =>
    arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
  );

  // Top Take: most net-positive top-level comment (needs net >= 3), excludes flagged
  const topTake = topLevel
    .filter((c) => !c.auto_flagged)
    .reduce((best, c) => (netScore(c) > (best ? netScore(best) : 2) ? c : best), null);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-6" data-testid="issue-detail">
      {/* Back */}
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-neutral hover:text-dark transition-colors mb-4" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> All debates
      </Link>

      {trending.length > 0 && <TrendingTicker items={trending} />}

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-card p-6 sm:p-8 mb-4">
        {topic.theme_image?.url ? (
          <div className="relative -mx-6 sm:-mx-8 -mt-6 sm:-mt-8 mb-5 h-48 sm:h-60 overflow-hidden rounded-t-2xl bg-gradient-to-br from-view-a/30 to-view-b/30" data-testid="debate-hero-image">
            <img src={topic.theme_image.url} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-black/35" />
            <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/55 backdrop-blur-md pl-1.5 pr-2.5 py-1 rounded-full">
              <CategoryBadge category={topic.category} />
              <span className="flex items-center gap-1 text-[11px] text-white font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" /> LIVE
              </span>
            </div>
            {topic.theme_image.credit && (
              <span className="absolute bottom-1.5 right-2.5 text-[9px] text-white/70">Photo: {topic.theme_image.credit}</span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-3">
            <CategoryBadge category={topic.category} />
            <span className="flex items-center gap-1 text-[11px] text-success font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" /> LIVE
            </span>
          </div>
        )}
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-dark leading-tight mb-6" data-testid="topic-question">
          {topic.question}
        </h1>

        {/* THE FLIP */}
        <div className="relative flex justify-center mb-6">
          <VoteBurst trigger={voteAnim} color={kickSide === "b" ? "#FFC300" : "#7B2CBF"} />
          <FloatingPlusOne trigger={voteAnim} color={kickSide === "b" ? "#B8860B" : "#7B2CBF"} />
          <TheFlip votesA={topic.votes_a || 0} votesB={topic.votes_b || 0} voteAnim={voteAnim} kickSide={kickSide} userSide={userVote} commentsA={commentsA} commentsB={commentsB} labelA={topic.side_a_label} labelB={topic.side_b_label} />
        </div>

        {/* Vote panels */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Side A */}
          <button
            onClick={() => handleVote("a")}
            disabled={voting}
            className={`relative text-left p-4 rounded-xl border-2 transition-all duration-300 ${
              userVote === "a"
                ? "border-view-a bg-view-a-light shadow-md"
                : "border-gray-200 hover:border-view-a/50 hover:bg-view-a-light/50"
            }`}
            data-testid="vote-side-a-button"
          >
            {userVote === "a" && (
              <span className="absolute top-3 right-3 text-[10px] font-bold text-view-a bg-white px-2 py-0.5 rounded-full shadow-sm">YOUR PICK</span>
            )}
            <span className="text-xs font-bold text-view-a tracking-wider uppercase">{topic.side_a_label}</span>
            <p className="text-sm text-dark/80 mt-1.5 leading-relaxed">{topic.side_a_text}</p>
          </button>

          {/* Side B */}
          <button
            onClick={() => handleVote("b")}
            disabled={voting}
            className={`relative text-left p-4 rounded-xl border-2 transition-all duration-300 ${
              userVote === "b"
                ? "border-view-b bg-view-b-light shadow-md"
                : "border-gray-200 hover:border-view-b/50 hover:bg-view-b-light/50"
            }`}
            data-testid="vote-side-b-button"
          >
            {userVote === "b" && (
              <span className="absolute top-3 right-3 text-[10px] font-bold text-view-b bg-white px-2 py-0.5 rounded-full shadow-sm">YOUR PICK</span>
            )}
            <span className="text-xs font-bold text-view-b tracking-wider uppercase">{topic.side_b_label}</span>
            <p className="text-sm text-dark/80 mt-1.5 leading-relaxed">{topic.side_b_text}</p>
          </button>
        </div>

        {/* Share actions */}
        <ShareBar topic={topic} topicId={topicId} pctA={pctA} pctB={pctB} shareCardRef={shareCardRef} />
      </div>

      {/* Next-debate momentum loop */}
      {(() => {
        const next = trending.find((t) => t.topic_id !== topicId && !getVote(t.topic_id))
          || trending.find((t) => t.topic_id !== topicId);
        if (!next) return null;
        const nImg = next.theme_image?.url;
        return (
          <Link to={`/issue/${next.topic_id}`} data-testid="next-debate-card"
            className="group block relative overflow-hidden rounded-2xl shadow-card hover:shadow-card-hover transition-shadow mb-4"
            style={{ background: "linear-gradient(120deg,#0D0F17,#161923 60%,#7B2CBF 150%)" }}>
            {nImg && <img src={nImg} alt="" referrerPolicy="no-referrer" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-25 transition-opacity" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            <div className="relative p-5 sm:p-6 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[11px] font-bold tracking-wider uppercase text-amber-300">Next Debate</span>
                  {sessionStreak > 1 && (
                    <span className="flex items-center gap-1 text-[11px] font-bold text-orange-300" data-testid="session-streak">
                      <Flame className="w-3.5 h-3.5" /> {sessionStreak} in a row
                    </span>
                  )}
                </div>
                <p className="font-heading text-lg sm:text-xl text-white leading-tight line-clamp-2">{next.question}</p>
                <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-amber-300">
                  {userVote ? "Keep the streak going" : "Cast your vote"} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
              <div className="shrink-0 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <ArrowRight className="w-5 h-5 text-white" />
              </div>
            </div>
          </Link>
        );
      })()}

      {/* Full long-form debate with fade/blur reading mode */}
      <DebateEssays topic={topic} topicId={topicId} api={api} userVote={userVote} />

      {/* Debate Room — anonymous live vote feed */}
      <DebateRoom topic={topic} />

      {/* Mobile quick-nav to comments */}
      <button
        onClick={() => document.getElementById("comments")?.scrollIntoView({ behavior: "smooth" })}
        className="md:hidden fixed bottom-20 right-4 z-[60] inline-flex items-center gap-2 px-4 py-3 rounded-full bg-dark text-white shadow-lg active:scale-95 transition-transform"
        data-testid="jump-to-comments-btn"
        aria-label="Jump to comments"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="text-sm font-semibold">{comments.length}</span>
      </button>

      {/* Comments */}
      <div id="comments" className="bg-white rounded-2xl shadow-card p-6" data-testid="comments-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl text-dark flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-neutral" /> DISCUSSION
            <span className="text-sm font-body text-neutral font-normal">({comments.length})</span>
          </h2>
          <div className="flex gap-1">
            {["top", "new"].map((s) => (
              <button
                key={s}
                onClick={() => setCommentSort(s)}
                className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
                  commentSort === s ? "bg-dark text-white" : "text-neutral hover:text-dark"
                }`}
                data-testid={`sort-${s}`}
              >
                {s === "top" ? "Top" : "New"}
              </button>
            ))}
          </div>
        </div>

        {user && userVote && (
          <div className="mb-4 p-3 rounded-xl bg-gray-50 border border-gray-100" data-testid="comment-progress">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-semibold text-dark">Your side: <span className={userVote === "a" ? "text-view-a" : "text-view-b"}>{userVote === "a" ? topic.side_a_label : topic.side_b_label}</span></span>
              <span className="text-neutral">{toNext} more comment{toNext !== 1 ? "s" : ""} = +1 vote</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${userVote === "a" ? "bg-view-a" : "bg-view-b"}`} style={{ width: `${progressPct}%` }} />
            </div>
            <p className="text-[10px] text-neutral mt-1">{Math.round(mySideComments * 10) / 10} comment-points supporting this view · 5 points = +1 vote on the compass (replies count as {REPLY_WEIGHT})</p>
          </div>
        )}

        {/* Add comment */}
        {user ? (
          <form onSubmit={handleComment} className="flex gap-3 mb-4 pb-4 border-b border-gray-100" data-testid="comment-form">
            <UserAvatar user={user} size="w-8 h-8" textSize="text-[10px]" />
            <div className="flex-1 flex gap-2">
              <input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Share your perspective..."
                className="flex-1 text-sm px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-view-a/30 focus:border-view-a transition-all"
                data-testid="comment-input"
              />
              <button
                type="submit"
                disabled={!commentText.trim() || submittingComment}
                className="p-2.5 bg-view-a text-white rounded-xl hover:bg-view-a-dark transition-colors disabled:opacity-40"
                data-testid="comment-submit-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        ) : (
          <p className="text-sm text-neutral mb-4 pb-4 border-b border-gray-100">Sign in to join the discussion.</p>
        )}

        {/* Comment list */}
        {topTake && (
          <div className="mb-4 p-4 rounded-xl border-2 border-amber-300 dark:border-amber-400/30 bg-gradient-to-br from-amber-50 to-white dark:from-amber-400/[0.08] dark:to-[#161923] shadow-sm" data-testid="top-take">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="text-[11px] font-bold tracking-wider uppercase text-amber-700 dark:text-amber-300">Top Take</span>
              <span className="text-[10px] text-neutral ml-auto font-semibold">+{netScore(topTake)} net reactions</span>
            </div>
            <div className="flex gap-3">
              <UserAvatar user={topTake.user} size="w-8 h-8" textSize="text-[10px]" />
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-dark">{topTake.user?.name || "Anonymous"}</span>
                  {topTake.voted_side && (
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${topTake.voted_side === "a" ? "text-view-a" : "text-view-b"}`}>{topTake.voted_side === "a" ? topic.side_a_label : topic.side_b_label}</span>
                  )}
                </div>
                <p className="text-sm text-dark/80 leading-relaxed line-clamp-3 whitespace-pre-wrap break-words">{topTake.text}</p>
              </div>
            </div>
          </div>
        )}

        {topLevel.length === 0 ? (
          <p className="text-center text-sm text-neutral py-6">No comments yet. Be the first to share your perspective.</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {topLevel.map((c) => (
              <CommentItem
                key={c.comment_id}
                comment={c}
                replies={repliesByParent[c.comment_id]}
                onReact={handleReact}
                onReply={handleReply}
                onReport={handleReport}
                onDelete={canModerate ? handleDeleteComment : null}
                sideLabelA={topic.side_a_label}
                sideLabelB={topic.side_b_label}
                user={user}
              />
            ))}
          </div>
        )}
      </div>

      {/* Hidden share card for image export */}
      <div style={{ position: "fixed", left: "-9999px", top: 0 }}>
        <div
          ref={shareCardRef}
          style={{
            width: "600px", padding: "48px 44px",
            background: "linear-gradient(135deg, #172033 0%, #1e293b 60%, #0f172a 100%)",
            fontFamily: "Inter, sans-serif", position: "relative", overflow: "hidden",
          }}
        >
          {topic.theme_image?.url && (
            <img src={topic.theme_image.url} crossOrigin="anonymous" alt=""
              style={{ width: "calc(100% + 88px)", height: "200px", objectFit: "cover", margin: "-48px -44px 24px", display: "block" }} />
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "28px" }}>
            <img src="/fv-logo.png" crossOrigin="anonymous" alt="" style={{ width: "38px", height: "38px", borderRadius: "10px", objectFit: "cover", display: "block" }} />
            <span style={{ color: "#fff", fontFamily: "'Outfit',sans-serif", fontWeight: 800, fontSize: "22px", letterSpacing: "0.3px" }}>Flipped<span style={{ color: "#FFC300" }}>View</span></span>
          </div>

          <p style={{ color: "#94A3B8", fontSize: "13px", fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", margin: "0 0 12px" }}>{topic.category}</p>
          <h2 style={{ color: "#fff", fontFamily: "'Outfit',sans-serif", fontWeight: 800, fontSize: "40px", lineHeight: 1.08, margin: "0 0 32px", letterSpacing: "0.2px" }}>{topic.question}</h2>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "12px" }}>
            <div>
              <p style={{ color: "#B07CE6", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", margin: "0 0 4px" }}>{topic.side_a_label}</p>
              <p style={{ color: "#9D4EDD", fontFamily: "'Outfit',sans-serif", fontWeight: 800, fontSize: "52px", lineHeight: 0.9, margin: 0 }}>{pctA}%</p>
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{ color: "#FFE07A", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", margin: "0 0 4px" }}>{topic.side_b_label}</p>
              <p style={{ color: "#FFC300", fontFamily: "'Outfit',sans-serif", fontWeight: 800, fontSize: "52px", lineHeight: 0.9, margin: 0 }}>{pctB}%</p>
            </div>
          </div>

          <div style={{ display: "flex", height: "16px", borderRadius: "8px", overflow: "hidden", background: "rgba(255,255,255,0.1)", marginBottom: "16px" }}>
            <div style={{ width: `${pctA}%`, background: "#7B2CBF" }} />
            <div style={{ width: `${pctB}%`, background: "#FFC300" }} />
          </div>

          <p style={{ color: "#64748B", fontSize: "14px", fontWeight: 600, margin: 0 }}>{total.toLocaleString()} votes · Where do you stand?</p>
        </div>
      </div>
    </div>
  );
}
